/**
 * HarvestModal — Plaid-style browser-in-browser overlay for CampMinder credential harvest.
 *
 * Full implementation for PR 3.
 *
 * Mockups (v1 — approved):
 *   designs/ai-dashboard-platform/2026-04-22-harvest-modal-launch-v1.html    (Screen 7)
 *   designs/ai-dashboard-platform/2026-04-22-harvest-modal-live-view-v1.html (Screen 8)
 *   designs/ai-dashboard-platform/2026-04-22-harvest-modal-progress-narration-v1.html (Screen 9)
 *   designs/ai-dashboard-platform/2026-04-22-harvest-modal-multi-clientid-picker-v1.html (Screen 10)
 *   designs/ai-dashboard-platform/2026-04-22-harvest-modal-success-v1.html   (Screen 11)
 *
 * Brand guide: §17 (Harvest Modal Pattern)
 *
 * Architecture (§3d.2):
 *  - Modal shell stays mounted across all 5 states (launching → login → picking_camp → harvest → success)
 *  - Iframe element is mounted ONCE during 'login', hidden via `display: none`
 *    (NOT unmounted) during 'picking_camp' and 'harvest'. Re-shown if needed.
 *  - DO NOT unmount/remount the iframe — it tears down the Browserbase session.
 *  - Progress narration steps are LOCAL component state derived from harvestContentState changes.
 *  - Slow-phase hint (>15 seconds on any step) tracked via useRef + setInterval.
 *  - Failure states dismiss the modal and route to the chat surface (CredentialPickerHarvestBlock).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Check,
  CheckCircle2,
  ChevronRight,
  Info,
  Loader2,
  Lock,
  RotateCcw,
  ShieldCheck,
  X,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { AppDispatch, RootState } from '@/redux'
import {
  dashboardCreationActions,
  selectHarvestCampName,
  selectHarvestCamps,
  selectHarvestContentState,
  selectHarvestKeyExpiresAt,
  selectHarvestLiveViewUrl,
} from '@/redux/slice/dashboardCreationSlice'
import { selectClientIdThunk, terminateHarvestThunk } from '@/services/HarvestService'
import { DashboardSocketService } from '@/services/DashboardSocketService'

// ─── Constants ─────────────────────────────────────────────────────────────────

const CAMPMINDER_ADMIN_URL =
  'https://system.campminder.com/ui/security/apiaccess/Admin'
const SLOW_PHASE_THRESHOLD_MS = 15_000

// ─── Progress step types ────────────────────────────────────────────────────────

type HarvestStepStatus = 'done' | 'active' | 'pending'

interface HarvestStep {
  id: string
  label: string
  detail?: string
  status: HarvestStepStatus
}

const INITIAL_PROGRESS_STEPS: HarvestStep[] = [
  { id: 'logged_in', label: 'Logged in to CampMinder', status: 'pending' },
  { id: 'creating_key', label: 'Creating your API key', status: 'pending' },
  { id: 'securing', label: 'Securing your credential', status: 'pending' },
  { id: 'done', label: 'Done', status: 'pending' },
]

// ─── Helpers ────────────────────────────────────────────────────────────────────

/** Camp emoji cycle — fallback visual differentiation when no emoji from server. */
const CAMP_EMOJIS = ['🏕️', '⛺', '🌲', '🏞️', '🌄']

function formatExpiryDate(isoString: string | null): string {
  if (!isoString) return 'about 1 year from now'
  const d = new Date(isoString)
  if (isNaN(d.getTime())) return 'about 1 year from now'
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

// ─── Sub-components ─────────────────────────────────────────────────────────────

/** Stable modal header — shown across all content states. */
function ModalHeader({ onClose }: { onClose: () => void }) {
  return (
    <DialogHeader className="px-5 pt-5 pb-0 flex-shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {/* TCS logo mark */}
          <div
            className="w-8 h-8 rounded-[8px] bg-accent-background border border-[#BFDBFE] flex items-center justify-center flex-shrink-0"
            aria-hidden="true"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              className="text-accent"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </div>
          <ChevronRight size={14} className="text-muted-foreground" aria-hidden="true" />
          {/* CampMinder logo mark */}
          <div
            className="w-8 h-8 rounded-[8px] bg-[#F0FDF4] border border-[#DCFCE7] flex items-center justify-center flex-shrink-0"
            aria-hidden="true"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              className="text-[#16A34A]"
              aria-hidden="true"
            >
              <path d="M3 20L12 4l9 16H3z" />
            </svg>
          </div>
        </div>
        <DialogTitle className="text-sm font-semibold text-primary">
          Connecting CampMinder
        </DialogTitle>
        <Button
          variant="ghost"
          size="icon"
          className="w-8 h-8 rounded-lg border border-border bg-muted hover:bg-destructive-foreground hover:text-destructive hover:border-destructive/30 transition-colors"
          aria-label="Close"
          onClick={onClose}
        >
          <X size={15} aria-hidden="true" />
        </Button>
      </div>
    </DialogHeader>
  )
}

// ─── Launching state ────────────────────────────────────────────────────────────

interface LaunchingProps {
  onLogIn: () => void
  onPasteKey: () => void
}

function LaunchingContent({ onLogIn, onPasteKey }: LaunchingProps) {
  return (
    <div className="px-6 pt-6 pb-6 flex flex-col gap-5" data-testid="harvest-launching-content">
      {/* Illustration */}
      <div className="flex items-center justify-center gap-4 mb-1" aria-hidden="true">
        <div className="w-14 h-14 rounded-[14px] bg-accent-background border-[1.5px] border-[#BFDBFE] flex items-center justify-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="text-accent"
            aria-hidden="true"
          >
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </div>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-muted-foreground"
          aria-hidden="true"
        >
          <path d="M8 3H5a2 2 0 00-2 2v14c0 1.1.9 2 2 2h3M16 3h3a2 2 0 012 2v14a2 2 0 01-2 2h-3M12 8l4 4-4 4M8 12h8" />
        </svg>
        <div className="w-14 h-14 rounded-[14px] bg-[#F0FDF4] border-[1.5px] border-[#DCFCE7] flex items-center justify-center">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            className="text-[#16A34A]"
            aria-hidden="true"
          >
            <path d="M3 20L12 4l9 16H3z" />
          </svg>
        </div>
      </div>

      {/* Heading + description */}
      <div className="text-center">
        <h2 className="text-[17px] font-bold text-primary mb-2.5">Log in to CampMinder</h2>
        <p className="text-sm text-secondary leading-relaxed">
          You&apos;re about to log into CampMinder directly. TCS will set up an API key named{' '}
          <strong className="text-primary">&ldquo;TheCampStack&rdquo;</strong> on your behalf. You
          can review or revoke it anytime in CampMinder → API Access Admin.
        </p>
      </div>

      {/* Trust bullets */}
      <ul className="flex flex-col gap-2.5" aria-label="Security guarantees">
        {[
          {
            text: (
              <>
                <strong className="text-primary">You log in directly to CampMinder</strong> — TCS
                never sees or stores your password.
              </>
            ),
          },
          {
            text: (
              <>
                <strong className="text-primary">One API key created on your behalf</strong> — named
                &ldquo;TheCampStack,&rdquo; visible in your CampMinder admin.
              </>
            ),
          },
          {
            text: (
              <>
                <strong className="text-primary">Works for ~1 year</strong> — you won&apos;t need
                to log in again until the key expires.
              </>
            ),
          },
        ].map(({ text }, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <div
              className="w-5 h-5 rounded-full bg-[#DCFCE7] flex items-center justify-center flex-shrink-0 mt-0.5"
              aria-hidden="true"
            >
              <Check size={11} className="text-[#16A34A]" aria-hidden="true" />
            </div>
            <p className="text-[13px] text-secondary leading-relaxed">{text}</p>
          </li>
        ))}
      </ul>

      {/* Pre-launch trust banner */}
      <div
        className="flex items-start gap-2.5 p-3 bg-success/10 border border-success/20 rounded-lg"
        role="note"
        aria-label="Security note"
      >
        <ShieldCheck size={14} className="text-success flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-[13px] text-success font-medium leading-relaxed">
          You&apos;re about to log into CampMinder directly.{' '}
          <strong>TCS never stores your CampMinder password.</strong>
        </p>
      </div>

      {/* CTA */}
      <Button
        className="w-full font-semibold"
        aria-label="Open CampMinder login in secure browser"
        onClick={onLogIn}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="mr-1.5"
          aria-hidden="true"
        >
          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
        Log in to CampMinder
      </Button>

      <p className="text-center text-[13px] text-muted-foreground">
        or{' '}
        <button
          type="button"
          className="text-accent underline hover:no-underline font-medium"
          onClick={onPasteKey}
          aria-label="Paste an API key you already have"
        >
          Paste an API key you already have
        </button>
      </p>
    </div>
  )
}

// ─── Live view (login) state ────────────────────────────────────────────────────

interface LiveViewProps {
  iframeUrl: string | null
}

/** Rendered when harvestContentState === 'login'. Iframe is always mounted here. */
function LiveViewChrome({ iframeUrl }: LiveViewProps) {
  return (
    <>
      {/* Trust strip */}
      <div
        className="flex items-center gap-2 px-4 py-2 bg-success/10 border-b border-success/20 text-success text-xs font-medium flex-shrink-0"
        role="note"
        aria-label="Security indicator"
      >
        <ShieldCheck size={14} aria-hidden="true" />
        <span>Logging into campminder.com — TCS never stores your password</span>
        <span className="ml-auto flex items-center gap-1 bg-success/15 px-2 py-0.5 rounded text-xs">
          <Lock size={10} aria-hidden="true" />
          system.campminder.com
        </span>
      </div>

      {/* Instruction bar */}
      <div
        className="bg-primary text-white px-4 py-2.5 text-[13px] text-center flex-shrink-0"
        role="note"
      >
        <strong>Log in to your CampMinder account below.</strong> TCS will handle the rest
        automatically.
      </div>

      {/* Live view iframe container */}
      <div
        className="flex-1 overflow-hidden bg-muted"
        aria-label="CampMinder login — interact directly"
      >
        {iframeUrl ? (
          <iframe
            src={iframeUrl}
            className="w-full h-full border-0"
            title="CampMinder secure login"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
            allow="clipboard-read; clipboard-write"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 size={24} className="text-muted-foreground animate-spin" aria-hidden="true" />
          </div>
        )}
      </div>
    </>
  )
}

// ─── Progress narration ────────────────────────────────────────────────────────

interface ProgressNarrationProps {
  steps: HarvestStep[]
  showSlowHint: boolean
}

function ProgressNarration({ steps, showSlowHint }: ProgressNarrationProps) {
  return (
    <div className="px-6 pt-6 pb-6 flex flex-col" data-testid="harvest-progress-content">
      <p className="text-base font-bold text-primary text-center mb-1">
        Setting up your connection...
      </p>
      <p className="text-[13px] text-secondary text-center mb-6">
        You&apos;re logged in — handling the rest now.
      </p>

      {/* Slow-phase hint — rendered when any step persists >15s */}
      {showSlowHint && (
        <div
          className="flex items-start gap-2 p-3 bg-accent-background border border-accent/20 rounded-lg text-xs text-accent mb-5"
          role="status"
          aria-live="polite"
        >
          <Info size={14} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>CampMinder can be slow — this is normal. We&apos;re still working on it.</span>
        </div>
      )}

      {/* Step list */}
      <div
        className="flex flex-col gap-0"
        role="list"
        aria-live="polite"
        aria-label="Connection setup progress"
      >
        {steps.map((step, i) => (
          <div key={step.id} className="flex gap-3" role="listitem">
            {/* Connector column */}
            <div className="flex flex-col items-center w-8 flex-shrink-0">
              {/* Status icon */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${
                  step.status === 'done'
                    ? 'bg-[#DCFCE7] border-[#DCFCE7]'
                    : step.status === 'active'
                      ? 'bg-accent-background border-accent'
                      : 'bg-muted border-border'
                }`}
                aria-label={`${step.label} — ${step.status}`}
              >
                {step.status === 'done' && (
                  <Check size={14} className="text-[#16A34A]" aria-hidden="true" />
                )}
                {step.status === 'active' && (
                  <Loader2 size={14} className="text-accent animate-spin" aria-hidden="true" />
                )}
                {step.status === 'pending' && (
                  <div className="w-2 h-2 rounded-full bg-muted-foreground" aria-hidden="true" />
                )}
              </div>
              {/* Vertical connector line (not on last item) */}
              {i < steps.length - 1 && (
                <div
                  className={`w-0.5 flex-1 mt-1 mb-1 ${step.status === 'done' ? 'bg-[#DCFCE7]' : 'bg-border'}`}
                  aria-hidden="true"
                />
              )}
            </div>
            {/* Step content */}
            <div className="pb-4 pt-1.5">
              <p
                className={`text-sm font-semibold leading-tight ${
                  step.status === 'done'
                    ? 'text-[#16A34A]'
                    : step.status === 'active'
                      ? 'text-primary'
                      : 'text-muted-foreground font-medium'
                }`}
              >
                {step.label}
              </p>
              {step.detail && (
                <p className="text-xs text-secondary mt-0.5 leading-relaxed">{step.detail}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Camp picker ────────────────────────────────────────────────────────────────

interface CampPickerProps {
  camps: { id: string; displayName: string }[]
  token: string
  sessionId: string | null
  onClose: () => void
}

function CampPickerContent({ camps, token, sessionId, onClose }: CampPickerProps) {
  const dispatch: AppDispatch = useDispatch()
  const [selectedId, setSelectedId] = useState<string>(camps[0]?.id ?? '')

  const handleConfirm = () => {
    if (!sessionId || !selectedId) return
    void (dispatch as (action: unknown) => Promise<void>)(selectClientIdThunk(token, sessionId, selectedId))
  }

  const selectedCamp = camps.find(c => c.id === selectedId)

  return (
    <div className="px-6 pt-5 pb-6" data-testid="harvest-camp-picker-content">
      {/* Mini-steps progress */}
      <div
        className="flex items-center gap-1.5 mb-5 justify-center text-xs"
        role="status"
        aria-label="Progress: logged in, API key created, now picking camp"
      >
        <span className="flex items-center gap-1 text-success font-medium">
          <Check size={12} aria-hidden="true" /> Logged in
        </span>
        <ChevronRight size={12} className="text-muted-foreground" aria-hidden="true" />
        <span className="flex items-center gap-1 text-success font-medium">
          <Check size={12} aria-hidden="true" /> API key created
        </span>
        <ChevronRight size={12} className="text-muted-foreground" aria-hidden="true" />
        <span className="font-semibold text-primary">Pick your camp</span>
      </div>

      <h2 className="text-base font-bold text-primary text-center mb-1.5">
        Which camp is this connection for?
      </h2>
      <p className="text-[13px] text-secondary text-center mb-5 leading-relaxed">
        Your account has multiple camps. Pick the one you&apos;re building this dashboard for.
      </p>

      <div className="flex flex-col gap-2 mb-5" role="radiogroup" aria-label="Select a camp">
        {camps.map((camp, idx) => {
          const isSelected = camp.id === selectedId
          return (
            <button
              key={camp.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={camp.displayName}
              className={`flex items-center gap-3 px-3.5 py-3 rounded-[12px] border-[1.5px] w-full text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2 ${
                isSelected
                  ? 'border-accent bg-accent-background'
                  : 'border-border bg-card hover:border-accent hover:bg-accent-background'
              }`}
              onClick={() => setSelectedId(camp.id)}
            >
              <div
                className={`w-9 h-9 rounded-[10px] border flex items-center justify-center flex-shrink-0 text-base ${
                  isSelected ? 'bg-accent-background border-[#BFDBFE]' : 'bg-muted border-border'
                }`}
                aria-hidden="true"
              >
                {CAMP_EMOJIS[idx % CAMP_EMOJIS.length]}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-semibold leading-tight ${isSelected ? 'text-accent' : 'text-primary'}`}
                >
                  {camp.displayName}
                </p>
                {/*
                  Client ID line is intentionally hidden when id === displayName.
                  CampMinder's post-Auth0 v2/home camp dropdown exposes no
                  per-camp identifier in the DOM (no clientId in href, no
                  data-attribute), so the SDK uses the camp label as a quasi-id
                  for click-resume routing. Surfacing "Client ID: <camp name>"
                  in the UI confused users (CEO 2026-04-28). The real CampMinder
                  client ID is resolved server-side by validateJwt after the
                  click-switch lands. If a future tenant's DOM exposes a real
                  ID and id !== displayName, fall through to render it.
                */}
                {camp.id !== camp.displayName && (
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">
                    Client ID: {camp.id}
                  </p>
                )}
              </div>
              {/* Radio indicator */}
              <div
                className={`w-[18px] h-[18px] rounded-full border flex-shrink-0 flex items-center justify-center ${
                  isSelected ? 'bg-accent border-accent' : 'bg-card border-border'
                }`}
                aria-hidden="true"
              >
                {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
              </div>
            </button>
          )
        })}
      </div>

      <div className="flex flex-col gap-2">
        <Button
          className="w-full font-semibold"
          disabled={!selectedId}
          onClick={handleConfirm}
          aria-label={selectedCamp ? `Connect ${selectedCamp.displayName}` : 'Connect this camp'}
        >
          {selectedCamp ? `Connect ${selectedCamp.displayName}` : 'Connect this camp'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="w-full text-secondary"
          onClick={onClose}
          aria-label="Cancel and go back"
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}

// ─── Success screen ────────────────────────────────────────────────────────────

interface SuccessProps {
  expiresAt: string | null
  campName: string | null
  onContinue: () => void
}

function SuccessContent({ expiresAt, campName, onContinue }: SuccessProps) {
  const expiryText = formatExpiryDate(expiresAt)

  return (
    <div className="px-6 pt-6 pb-6 overflow-y-auto" data-testid="harvest-success-content">
      {/* Success icon */}
      <div className="flex justify-center mb-4" aria-hidden="true">
        <div className="w-16 h-16 rounded-full bg-[#DCFCE7] border-[3px] border-[#BBF7D0] flex items-center justify-center">
          <CheckCircle2 size={32} className="text-[#16A34A]" aria-hidden="true" />
        </div>
      </div>

      <h2 className="text-lg font-bold text-primary text-center mb-1">CampMinder connected</h2>
      {campName && (
        <p className="text-[13px] text-secondary text-center mb-5">{campName} is all set.</p>
      )}
      {!campName && (
        <p className="text-[13px] text-secondary text-center mb-5">All set.</p>
      )}

      {/* 4 content blocks (§17 success screen spec) */}
      <div className="flex flex-col gap-3 mb-5" role="list">
        {/* Block 1: Status + expiry */}
        <div
          className="p-3.5 bg-success/10 border border-success/20 rounded-[10px]"
          role="listitem"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide text-success mb-1">
            Connection status
          </p>
          <p className="text-sm font-semibold text-success leading-relaxed">
            CampMinder connected. Active until{' '}
            <strong>{expiresAt ? expiryText : 'about 1 year from now'}</strong>.
          </p>
        </div>

        {/* Block 2: Expectation-setting (always "remember", never "store") */}
        <div className="p-3.5 bg-muted border border-border rounded-[10px]" role="listitem">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
            What happens next
          </p>
          <p className="text-sm text-primary leading-relaxed">
            We&apos;ll <strong>remember</strong> this connection until{' '}
            {expiresAt ? expiryText : 'about 1 year from now'} — you won&apos;t need to log in
            again until then.
          </p>
        </div>

        {/* Block 3: API key transparency */}
        <div className="p-3.5 bg-muted border border-border rounded-[10px]" role="listitem">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
            What we created
          </p>
          <p className="text-sm text-primary leading-relaxed">
            We created an API key named <strong>&ldquo;TheCampStack&rdquo;</strong> for this
            connection.{' '}
            <a
              href={CAMPMINDER_ADMIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline hover:no-underline"
              aria-label="View TheCampStack API key in CampMinder Admin — opens in new tab"
            >
              View it in CampMinder →
            </a>
          </p>
        </div>

        {/* Block 4: Scope disclosure */}
        <div className="p-3.5 bg-muted border border-border rounded-[10px]" role="listitem">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
            What&apos;s connected
          </p>
          <p className="text-sm text-primary leading-relaxed mb-2">
            TheCampStack can now pull your enrollments, campers, sessions, financials, staff, and
            bunks.
          </p>
          <span className="inline-flex items-center gap-1.5 text-xs text-accent bg-accent-background px-2.5 py-0.5 rounded-full font-semibold">
            <Info size={11} aria-hidden="true" />
            Custom reports are coming in a future update
          </span>
        </div>
      </div>

      {/* Trust reinforcement footer */}
      <div
        className="flex items-center gap-2 p-2.5 bg-muted border border-border rounded-lg mb-4"
        role="note"
        aria-label="Security confirmation"
      >
        <ShieldCheck size={14} className="text-success flex-shrink-0" aria-hidden="true" />
        <p className="text-xs text-secondary">Your CampMinder password was never saved by TCS.</p>
      </div>

      <Button
        className="w-full font-semibold"
        onClick={onContinue}
        aria-label="Continue building your dashboard"
      >
        <RotateCcw size={15} className="mr-1.5" aria-hidden="true" />
        Continue building your dashboard
      </Button>
    </div>
  )
}

// ─── HarvestModal ──────────────────────────────────────────────────────────────

interface HarvestModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  onPasteKey: () => void
  /**
   * When the modal is mounted from a dashboard creation flow, `onSuccess`
   * must advance the creation FSM to `awaiting_domain`. When mounted from
   * non-creation contexts (credential reconnect on the Credentials page,
   * sync-failed reconnect on the Canvas page), the FSM must NOT be touched —
   * otherwise a stale creation session gets corrupted the next time the user
   * creates a new dashboard.
   *
   * Defaults to `true` for backward compatibility with the creation flow.
   * Pass `false` from reconnect surfaces.
   */
  advanceCreationFlowOnSuccess?: boolean
}

export const HarvestModal = ({
  isOpen,
  onClose,
  onSuccess,
  onPasteKey,
  advanceCreationFlowOnSuccess = true,
}: HarvestModalProps) => {
  const dispatch: AppDispatch = useDispatch()
  const token = useSelector((state: RootState) => state.user.token ?? '')
  const contentState = useSelector(selectHarvestContentState)
  const liveViewUrl = useSelector(selectHarvestLiveViewUrl)
  const camps = useSelector(selectHarvestCamps)
  const expiresAt = useSelector(selectHarvestKeyExpiresAt)
  const campName = useSelector(selectHarvestCampName)
  const sessionId = useSelector((state: RootState) => state.dashboardCreation.harvestSessionId)

  // ── Progress narration steps (local state — derived from contentState changes) ──
  const [progressSteps, setProgressSteps] = useState<HarvestStep[]>(INITIAL_PROGRESS_STEPS)
  const [showSlowHint, setShowSlowHint] = useState(false)
  const activeStepStartRef = useRef<number | null>(null)
  const slowHintTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Whether the iframe has been mounted (stays true once 'login' is reached)
  const iframeMountedRef = useRef(false)
  if (contentState === 'login' || contentState === 'harvest' || contentState === 'picking_camp') {
    iframeMountedRef.current = true
  }

  // Reset steps and slow hint when modal closes / relaunches
  useEffect(() => {
    if (!isOpen) {
      setProgressSteps(INITIAL_PROGRESS_STEPS)
      setShowSlowHint(false)
      activeStepStartRef.current = null
      iframeMountedRef.current = false
      if (slowHintTimerRef.current) {
        clearInterval(slowHintTimerRef.current)
        slowHintTimerRef.current = null
      }
    }
  }, [isOpen])

  // Advance progress steps based on contentState transitions
  useEffect(() => {
    if (!isOpen) return

    // Map contentState → which step should be active
    // Derived from brand guide §17 and the 4-step list in the progress narration mockup:
    // Step 1: "Logged in to CampMinder"    → becomes 'done' when we move past 'login'
    // Step 2: "Creating your API key"      → active during 'harvest' (creating_key socket step)
    // Step 3: "Securing your credential"   → active during 'harvest' (securing socket step)
    //   Note: the modal doesn't distinguish creating_key vs securing — both are 'harvest' contentState
    //   We use time-sequenced advancement: creating_key first, then securing.
    //   The socket service will need to expose finer-grained events eventually.
    //   For now: 'harvest' = step 2 active; 'success' = all steps done.
    // Step 4: "Done"                        → active briefly before success screen
    if (contentState === 'harvest') {
      setProgressSteps(prev =>
        prev.map((s, i) => ({
          ...s,
          status: i === 0 ? 'done' : i === 1 ? 'active' : 'pending',
        }))
      )
      activeStepStartRef.current = Date.now()
    } else if (contentState === 'success') {
      setProgressSteps(prev => prev.map(s => ({ ...s, status: 'done' })))
      setShowSlowHint(false)
      activeStepStartRef.current = null
    }
  }, [contentState, isOpen])

  // Slow-phase hint: check every 5 seconds whether the active step has been running >15s
  useEffect(() => {
    if (slowHintTimerRef.current) {
      clearInterval(slowHintTimerRef.current)
      slowHintTimerRef.current = null
    }

    if (contentState === 'harvest' && isOpen) {
      slowHintTimerRef.current = setInterval(() => {
        if (
          activeStepStartRef.current &&
          Date.now() - activeStepStartRef.current > SLOW_PHASE_THRESHOLD_MS
        ) {
          setShowSlowHint(true)
        }
      }, 5000)
    } else {
      setShowSlowHint(false)
    }

    return () => {
      if (slowHintTimerRef.current) {
        clearInterval(slowHintTimerRef.current)
        slowHintTimerRef.current = null
      }
    }
  }, [contentState, isOpen])

  // Defensive: picking_camp with empty camps array is a backend bug
  // (backend should not emit picking_camp for single-camp users — TDD line 1262 +
  // auth memo §4.7). Recover gracefully by transitioning back to 'harvest' state.
  useEffect(() => {
    if (contentState === 'picking_camp' && camps.length === 0) {
      console.warn('HarvestModal: picking_camp with empty camps — defensive transition to harvest')
      dispatch(dashboardCreationActions.setHarvestContentState('harvest'))
    }
  }, [contentState, camps.length, dispatch])

  const handleLogIn = useCallback(() => {
    // TODO: startHarvestThunk is dispatched by CredentialPickerHarvestBlock before opening
    // the modal. If liveViewUrl is already set (session started), transition to login.
    // If called from the Launch screen after session is already started, the modal already
    // has a live URL. This CTA is for the case where the session was started and the modal
    // transitions naturally.
    // For future: if startHarvestThunk hasn't been dispatched yet (liveViewUrl === null),
    // dispatch it here. For now, the parent is responsible for starting the session first.
    dispatch(dashboardCreationActions.setHarvestContentState('login'))
  }, [dispatch])

  const handleClose = useCallback(() => {
    if (sessionId && token && contentState !== 'launching' && contentState !== 'success') {
      // User closed mid-flow — terminate the session
      void (dispatch as (action: unknown) => Promise<void>)(terminateHarvestThunk(token, sessionId))
    } else {
      dispatch(dashboardCreationActions.clearHarvest())
    }
    onClose()
  }, [dispatch, sessionId, token, contentState, onClose])

  const handleSuccess = useCallback(() => {
    // Leave the harvest socket room so a future reconnect doesn't auto-rejoin
    // the now-completed session and accept stale events into the next harvest.
    // The handler in DashboardSocketService also calls leaveHarvestSession on
    // the terminal `complete` event, but the modal can be advanced by the user
    // (Continue button) before the event arrives — handle both paths.
    DashboardSocketService.leaveHarvestSession()
    // Advance creation flow: set flowState to 'awaiting_domain'.
    // Only fire when the modal is mounted from the creation flow — reconnect
    // flows (Credentials page, Canvas sync-failed) pass false because the
    // creation FSM is not active and mutating it corrupts the next creation.
    if (advanceCreationFlowOnSuccess) {
      dispatch(dashboardCreationActions.setFlowState('awaiting_domain'))
    }
    onSuccess()
    onClose()
  }, [dispatch, onSuccess, onClose, advanceCreationFlowOnSuccess])

  // Dialog width: 520px for launch/success, 600px for live-view/progress
  const isWide = contentState === 'login' || contentState === 'harvest'

  return (
    <Dialog open={isOpen} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent
        className={[
          'p-0 gap-0 flex flex-col overflow-hidden',
          isWide ? 'sm:max-w-[600px]' : 'sm:max-w-[520px]',
          // Mobile: full-screen bottom sheet
          'max-sm:fixed max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:rounded-t-2xl max-sm:rounded-b-none max-sm:max-w-full max-sm:h-[95vh] max-sm:translate-y-0 max-sm:top-auto',
          // Live view state gets a fixed height
          (contentState === 'login' || contentState === 'harvest') ? 'h-[85vh] max-h-[680px]' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-labelledby="harvest-modal-title"
        data-testid="harvest-modal"
      >
        <ModalHeader onClose={handleClose} />

        {/* ── Content area ─────────────────────────────────────────────── */}
        <div
          className={`flex-1 flex flex-col overflow-hidden ${contentState === 'success' ? 'overflow-y-auto' : ''}`}
        >
          {/* Trust strip + iframe — mounted once, shown/hidden via CSS */}
          {iframeMountedRef.current && (
            <div
              className="flex-1 flex flex-col overflow-hidden"
              style={{ display: contentState === 'login' ? 'flex' : 'none' }}
              aria-hidden={contentState !== 'login'}
            >
              <LiveViewChrome iframeUrl={liveViewUrl} />
            </div>
          )}

          {/* Progress narration — shown when in 'harvest' state */}
          {(contentState === 'harvest') && (
            <div className="flex-1 overflow-y-auto">
              <ProgressNarration steps={progressSteps} showSlowHint={showSlowHint} />
            </div>
          )}

          {/* Camp picker — shown when in 'picking_camp' state */}
          {contentState === 'picking_camp' && camps.length > 0 && (
            <div className="flex-1 overflow-y-auto">
              <CampPickerContent
                camps={camps}
                token={token}
                sessionId={sessionId}
                onClose={handleClose}
              />
            </div>
          )}

          {/* Success screen */}
          {contentState === 'success' && (
            <div className="flex-1 overflow-y-auto">
              <SuccessContent
                expiresAt={expiresAt}
                campName={campName}
                onContinue={handleSuccess}
              />
            </div>
          )}

          {/* Launch screen — default when no other state is active */}
          {contentState === 'launching' && (
            <div className="flex-1 overflow-y-auto">
              <LaunchingContent onLogIn={handleLogIn} onPasteKey={onPasteKey} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
